import React, { useEffect } from 'react';

import { useSnackbar } from 'notistack';

import LinearProgress from '@mui/material/LinearProgress';
import EditIcon from '@mui/icons-material/Edit';

import { fetchTicket, fetchWorkflow, resetTicket } from '../../state';
import {
    useSelectedTicketId,
    useTicketAsReport,
    useTicketDetailsStatus,
    useWorkflows,
} from '../../state/index.hooks';

import { useAppDispatch, useToolkitDispatch } from '../../../../hooks';

import TranslationHelper from '../../../../helpers/TranslationHelper';

import PaneHeader from '../../../../components/PaneHeader';
import IconButtonWithTooltip from '../../../../components/IconButtonWithTooltip';
import ReportPreview from '../../../../components/report/ReportPreview';
import { showTicketHistoryGridDialog } from '../../../../state/ui/dialog';
import GridIcon from '@mui/icons-material/GridOn';
import { errorMessageHandler } from '../../../../helpers/errorMessageHandler';

interface IOwnProps {
    handleChangeMode: (mode: 'preview' | 'edit') => void;
}

const TicketPreviewPane = ({ handleChangeMode }: IOwnProps) => {
    const toolkitDispatch = useToolkitDispatch();
    const selectedTicketId = useSelectedTicketId();
    const ticketStatus = useTicketDetailsStatus();
    const ticketReports = useTicketAsReport();
    const { enqueueSnackbar } = useSnackbar();

    const reports = ticketReports?.reports || null;
    const workflows = useWorkflows();
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (selectedTicketId) {
            toolkitDispatch(fetchTicket(selectedTicketId.toString()))
                .unwrap()
                .then((data) => {
                    const workflowId = workflows?.find(
                        (w) => w.name === data.workflow
                    )?.id;
                    workflowId && toolkitDispatch(fetchWorkflow(workflowId));
                })
                .catch((e) => {
                    const message = errorMessageHandler(e.status)();
                    enqueueSnackbar(message, {
                        variant: 'error',
                    });
                });
        }
    }, [selectedTicketId]);

    const handleOpenHistoryTicketGridDialog = () => {
        if (selectedTicketId) {
            dispatch(
                showTicketHistoryGridDialog({
                    ticketId: selectedTicketId,
                })
            );
        }
    };

    const renderRightCustomControls = () => {
        return (
            <>
                <IconButtonWithTooltip
                    title={TranslationHelper.translate('Show history')}
                    onClick={handleOpenHistoryTicketGridDialog}
                >
                    <GridIcon />
                </IconButtonWithTooltip>

                <IconButtonWithTooltip
                    title={TranslationHelper.translate('Edit')}
                    onClick={() => handleChangeMode('edit')}
                >
                    <EditIcon />
                </IconButtonWithTooltip>
            </>
        );
    };
    const onCloseClick = () => {
        toolkitDispatch(resetTicket());
    };

    return (
        <>
            <PaneHeader
                title={TranslationHelper.translate('Ticket details')}
                onCloseClick={onCloseClick}
                renderRightCustomControls={renderRightCustomControls}
            />
            {ticketStatus === 'loading' ? (
                <LinearProgress />
            ) : (
                <ReportPreview reports={reports} />
            )}
        </>
    );
};

export default TicketPreviewPane;
